/* eslint-disable no-prototype-builtins */
import Button from '@components/atoms/button'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { FastlyImage } from '@components/common/image'
import { COURSE_FIRST_FOLD } from '@lib/data/course-landing-page'
import { CourseNameType } from '@lib/types/course-landing-page'
import { useState } from 'react'
import { Download } from 'react-feather'
import Modal from '@components/common/modal'
import StudentCountriesOverview from '@components/home/student-countries-overview'
import Ratings from '@components/common/ratings'

const IMAGE_PATH = '/images/courses/'

type CourseCardProps = {
  shouldHideCardImage?: boolean
  courseName: CourseNameType
  ishideStarRating?: boolean
  ishideDownloadCurriculum?: boolean
  urlQuery?: string
}

const CourseCard = ({
  shouldHideCardImage,
  courseName,
  ishideDownloadCurriculum,
  ishideStarRating,
  urlQuery,
}: CourseCardProps) => {
  const [ismodalOpen, setModalOpen] = useState(false)
  const [videoURL, setVideoURL] = useState('')

  const coursesData = COURSE_FIRST_FOLD[courseName]
  const {
    THUNKABLE,
    ANIMATION,
    TURTLE,
    BOOTSTRAP,
    JAVASCRIPT,
    CSS,
    HTML,
    AICODE101,
    CODE_ORG,
    PICTOBLOX,
    TEACHABLE_MACHINE,
    GOOGLE_COLAB,
    NODE_JS,
    VISUAL_STUDIO_CODE,
    SQL,
    REACT_JS,
    GIT,
    GITHUB,
    TRINKET,
    PYGAME,
  } = COURSE_FIRST_FOLD
  const imageProps = {
    THUNKABLE: {
      path: `${IMAGE_PATH}thunkable/card-image`,
      altText: 'Thunkable course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Personalized 1-on-1 or group online Thunkable classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on Thunkable programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${THUNKABLE.activities} interactive activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced Thunkable learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    JAVA: {
      path: `${IMAGE_PATH}java/card-image`,
      altText: 'Java course for kids with Coding Instructor',
    },
    TRINKET: {
      path: `${IMAGE_PATH}trinket/card-image`,
      altText: 'Trinket course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Personalized 1-on-1 or group online Trinket classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on Trinket programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${TRINKET.activities} interactive activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced Trinket learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    PYGAME: {
      path: `${IMAGE_PATH}pygame/card-image`,
      altText: 'Pygame course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Personalized 1-on-1 or group online Pygame classes for kids',
        },
        {
          icon: 'learn',
          text:
            'Live instruction from expert Codingal instructors who make learning Python fun and interactive',
        },
        {
          icon: 'learn',
          text:
            'Hands-on Python programming experience with game-based learning integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${PYGAME.activities} interactive coding activities and real-world projects, including the Space Invader game built with Pygame`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org-accredited certificates recognizing your child’s coding and game development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to all course content for flexible, self-paced learning',
        },
        {
          icon: 'personalized',
          text:
            'Unlimited 1:1 live doubt-clearing sessions with instructors for personalized support',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth and creativity in coding and game development',
        },
      ],
    },
    ANIMATION: {
      path: `${IMAGE_PATH}animation/card-image`,
      altText: 'Animation course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Personalized 1-on-1 or group online Animation classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on Animation programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${ANIMATION.activities} interactive activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced Animation learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    TURTLE: {
      path: `${IMAGE_PATH}turtle/card-image`,
      altText: 'Turtle course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Personalized 1-on-1 or group online Python Turtle classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on Python Turtle programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${TURTLE.activities} interactive activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced Python Turtle learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    MIT_APP_INVENTOR: {
      path: `${IMAGE_PATH}mit-app-inventor/card-image`,
      altText: 'MIT App Inventor course for kids with Coding Instructor',
    },
    BOOTSTRAP: {
      path: `${IMAGE_PATH}bootstrap/card-image`,
      altText: 'Bootstrap course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Personalized 1-on-1 or group online Bootstrap classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on Bootstrap programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${BOOTSTRAP.activities} interactive activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced Bootstrap learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    JAVASCRIPT: {
      path: `${IMAGE_PATH}javascript/card-image`,
      altText: 'Javascript course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Personalized 1-on-1 or group online  Javascript classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on Javascript programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${JAVASCRIPT.activities} interactive activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced Javascript learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    CSS: {
      path: `${IMAGE_PATH}css/card-image`,
      altText: 'CSS course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Choose from group or 1-on-1 personalized online CSS classes for kids',
        },
        {
          icon: 'learn',
          text:
            'Learn live with expert Computer Science teachers in every kids CSS class',
        },
        {
          icon: 'activity',
          text: `Engage in ${CSS.activities} interactive
              activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'Students earn STEM.org accredited certificates, showcasing skills in front-end development for kids',
        },
        {
          icon: 'infinity',
          text:
            'Enjoy lifetime access to course content for self-paced CSS learning',
        },
        {
          icon: 'personalized',
          text: 'Get doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Receive monthly progress reports to track your child’s growth in creative coding for kids',
        },
      ],
    },
    HTML: {
      path: `${IMAGE_PATH}html/card-image`,
      altText: 'HTML course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Personalized 1-on-1 or group online AI courses for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text: 'Hands-on AI for kids learning integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${HTML.activities} interactive activities and projects to explore artificial intelligence for kids`,
        },
        {
          icon: 'certificate',
          text: 'STEM.org accredited AI & ML certificates for kids',
        },
        {
          icon: 'infinity',
          text: 'Lifetime access to course content for self-paced learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text: 'Monthly progress reports to track your child’s growth',
        },
      ],
    },
    AICODE101: {
      path: `${IMAGE_PATH}ai-code-101/card-image`,
      altText: 'AI Code 101 course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Personalized 1-on-1 or group online AI courses for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text: 'Hands-on AI for kids learning integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${AICODE101.activities} interactive activities and projects to explore artificial intelligence for kids`,
        },
        {
          icon: 'certificate',
          text: 'STEM.org accredited AI & ML certificates for kids',
        },
        {
          icon: 'infinity',
          text: 'Lifetime access to course content for self-paced learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text: 'Monthly progress reports to track your child’s growth',
        },
        {
          icon: 'lifetime-access',
          text: 'Lifetime access to course content',
        },
      ],
    },
    CODE_ORG: {
      path: `${IMAGE_PATH}code-org/card-image`,
      altText: 'AI Code 101 course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Choose from group or 1-on-1 personalized AI & coding lessons for kids',
        },
        {
          icon: 'learn',
          text: 'Learn live with expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Code.org for kids is seamlessly integrated into the curriculum, providing hands-on AI & coding experience',
        },
        {
          icon: 'activity',
          text: `Engage in ${CODE_ORG.activities} activities & projects designed to enhance practical coding skills`,
        },
        {
          icon: 'certificate',
          text:
            'Students earn STEM.org accredited certificates, recognizing their achievements',
        },
        {
          icon: 'infinity',
          text:
            'Enjoy lifetime access to Code.org course content for continuous learning',
        },
        {
          icon: 'personalized',
          text: 'Get doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            "Receive monthly progress reports to track your child's growth and performance",
        },
        {
          icon: 'lifetime-access',
          text: 'Lifetime access to course content',
        },
      ],
    },
    PICTOBLOX: {
      path: `${IMAGE_PATH}pictoblox/card-image`,
      altText: 'PictoBlox course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Choose from group or 1-on-1 personalized AI & coding lessons',
        },
        {
          icon: 'learn',
          text: 'Learn live with expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'PictoBlox is seamlessly integrated into the curriculum, providing hands-on AI & coding experience',
        },
        {
          icon: 'activity',
          text: `Engage in ${PICTOBLOX.activities} activities & projects designed to enhance practical skills`,
        },
        {
          icon: 'certificate',
          text:
            'Students earn STEM.org accredited certificates, recognizing their achievements',
        },
        {
          icon: 'infinity',
          text:
            'Enjoy lifetime access to course content for continuous learning',
        },
        {
          icon: 'personalized',
          text: 'Get doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            "Receive monthly progress reports to track your child's growth and performance",
        },
        {
          icon: 'lifetime-access',
          text: 'Lifetime access to Pictoblox course content',
        },
      ],
    },
    TEACHABLE_MACHINE: {
      path: `${IMAGE_PATH}teachable-machine/card-image`,
      altText: 'Teachable Machine course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Choose from group or 1-on-1 personalized AI & coding lessons',
        },
        {
          icon: 'learn',
          text: 'Learn live with expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Teachable Machine is seamlessly integrated into the curriculum, providing hands-on AI & coding experience',
        },
        {
          icon: 'activity',
          text: `Engage in ${TEACHABLE_MACHINE.activities} activities & projects designed to enhance practical skills`,
        },
        {
          icon: 'certificate',
          text:
            'Students earn STEM.org accredited certificates, recognizing their achievements',
        },
        {
          icon: 'infinity',
          text:
            'Enjoy lifetime access to Code.org course content for continuous learning',
        },
        {
          icon: 'personalized',
          text: 'Get doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            "Receive monthly progress reports to track your child's growth and performance",
        },
        {
          icon: 'lifetime-access',
          text: 'Lifetime access to Teachable Machine course content',
        },
      ],
    },
    GOOGLE_COLAB: {
      path: `${IMAGE_PATH}google-colab/card-image`,
      altText: 'Google Colab course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Choose from group or 1-on-1 personalized AI & coding lessons',
        },
        {
          icon: 'learn',
          text: 'Learn live with expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Google Colab is seamlessly integrated into the curriculum, providing hands-on AI & coding experience',
        },
        {
          icon: 'activity',
          text: `Engage in ${GOOGLE_COLAB.activities} activities &  projects designed to enhance practical skills`,
        },
        {
          icon: 'certificate',
          text:
            'Students earn STEM.org accredited certificates, recognizing their achievements',
        },
        {
          icon: 'infinity',
          text:
            'Enjoy lifetime access to course content for continuous learning',
        },
        {
          icon: 'personalized',
          text: 'Get doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            "Receive monthly progress reports to track your child's growth and performance",
        },
        {
          icon: 'lifetime-access',
          text: 'Lifetime access to Google Colab course content',
        },
      ],
    },
    NODE_JS: {
      path: `${IMAGE_PATH}nodejs/card-image`,
      altText: 'Node.js course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Choose from group or 1-on-1 personalized AI & coding lessons',
        },
        {
          icon: 'learn',
          text: 'Learn live with expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Node.js is seamlessly integrated into the curriculum, providing hands-on AI & coding experience',
        },
        {
          icon: 'activity',
          text: `Engage in ${NODE_JS.activities} activities & projects designed to enhance practical skills`,
        },
        {
          icon: 'certificate',
          text:
            'Students earn STEM.org accredited certificates, recognizing their achievements',
        },
        {
          icon: 'infinity',
          text:
            'Enjoy lifetime access to course content for continuous learning',
        },
        {
          icon: 'personalized',
          text: 'Get doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            "Receive monthly progress reports to track your child's growth and performance",
        },
        {
          icon: 'lifetime-access',
          text: 'Lifetime access to Node.js course content',
        },
      ],
    },
    VISUAL_STUDIO_CODE: {
      path: `${IMAGE_PATH}vscode/card-image`,
      altText: 'Visual Studio Code course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Personalized 1-on-1 or group online coding classes using Visual Studio Code',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on experience using Visual Studio Code as a coding playground',
        },
        {
          icon: 'activity',
          text: `${VISUAL_STUDIO_CODE.activities} interactive activities and projects to explore text-based coding`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates recognizing technical skill development',
        },
        {
          icon: 'infinity',
          text: 'Lifetime access to Visual Studio Code learning resources',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track growth in coding proficiency',
        },
      ],
    },
    SQL: {
      path: `${IMAGE_PATH}sql/card-image`,
      altText: 'SQL course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text:
            'Personalized 1-on-1 or group online SQL coding lessons for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on SQL programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${SQL.activities} interactive activities and projects to explore SQL fundamentals`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates recognizing coding skills development',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to SQL learning resources for self-paced learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in coding proficiency',
        },
      ],
    },
    REACT_JS: {
      path: `${IMAGE_PATH}reactjs/card-image`,
      altText: 'React.Js course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Personalized 1-on-1 or group online React.js classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on React.js programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${REACT_JS.activities} interactive activities and projects to explore web design for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced React.js learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    GIT: {
      path: `${IMAGE_PATH}git/card-image`,
      altText: 'GIT course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Personalized 1-on-1 or group online GIT classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on GIT programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${GIT.activities} interactive activities and projects to explore version control for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text: 'Lifetime access to course content for self-paced GIT learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    GITHUB: {
      path: `${IMAGE_PATH}github/card-image`,
      altText: 'Github course for kids with Coding Instructor',
      includes: [
        {
          icon: 'session',
          text: 'Personalized 1-on-1 or group online GitHub classes for kids',
        },
        {
          icon: 'learn',
          text: 'Live instruction from expert Computer Science teachers',
        },
        {
          icon: 'learn',
          text:
            'Hands-on GitHub programming experience integrated into the curriculum',
        },
        {
          icon: 'activity',
          text: `${GITHUB.activities} interactive activities and projects to explore version control for kids`,
        },
        {
          icon: 'certificate',
          text:
            'STEM.org accredited certificates for front-end development skills',
        },
        {
          icon: 'infinity',
          text:
            'Lifetime access to course content for self-paced GitHub learning',
        },
        {
          icon: 'personalized',
          text: 'Doubt sessions with your teacher',
        },
        {
          icon: 'file',
          text:
            'Monthly progress reports to track your child’s growth in creative coding',
        },
      ],
    },
    PUBLIC_SPEAKING_GRADE_3_5: {
      path: `${IMAGE_PATH}public-speaking-and-communication/grade-3-5/card-image`,
      altText: 'Public speaking course for Grade 3-5 kids',
      includes: [
        {
          icon: 'session',
          text: 'Live interactive classes (1:1 private or small group)',
        },
        {
          icon: 'learn',
          text:
            'Speak with confidence by learning from live expert-led sessions',
        },
        {
          icon: 'modules',
          text: 'Speaking practice and activities in every class',
        },
        {
          icon: 'personalized',
          text: 'Real-time feedback from expert teachers',
        },
        {
          icon: 'activity',
          text: 'Projects and performance showcase',
        },
        {
          icon: 'infinity',
          text: 'Quizzes for skill boost',
        },
        {
          icon: 'certificate',
          text: 'Stem.org certificates on completion',
        },
        {
          icon: 'file',
          text: 'Monthly progress meets with teachers',
        },
        {
          icon: 'lifetime-access',
          text:
            'Lifetime access to all classes, resources and practice material',
        },
      ],
    },
    PUBLIC_SPEAKING_GRADE_1_2: {
      path: `${IMAGE_PATH}public-speaking-and-communication/grade-1-2/card-image`,
      altText: 'Public speaking course for Grade 1-2 kids',
      includes: [
        {
          icon: 'session',
          text: 'Live interactive classes (1:1 private or small group)',
        },
        {
          icon: 'learn',
          text:
            'Speak with confidence by learning from live expert-led sessions',
        },
        {
          icon: 'modules',
          text: 'Speaking practice and activities in every class',
        },
        {
          icon: 'personalized',
          text: 'Real-time feedback from expert teachers',
        },
        {
          icon: 'activity',
          text: 'Projects and performance showcase',
        },
        {
          icon: 'infinity',
          text: 'Quizzes for skill boost',
        },
        {
          icon: 'certificate',
          text: 'Stem.org certificates on completion',
        },
        {
          icon: 'file',
          text: 'Monthly progress meets with teachers',
        },
        {
          icon: 'lifetime-access',
          text:
            'Lifetime access to all classes, resources and practice material',
        },
      ],
    },
    CODING_GRADE_1_3: {
      path: `${IMAGE_PATH}coding-for-kids/grade-1-3/card-image`,
      altText: 'Coding course for Grade 1-3 kids',
      includes: [
        {
          icon: 'session',
          text: 'Live coding classes in 1:1 or small groups',
        },
        {
          icon: 'learn',
          text: 'Friendly teachers who make coding fun and easy',
        },
        {
          icon: 'modules',
          text: 'Hands-on games, stories, animations, and creative projects',
        },
        {
          icon: 'personalized',
          text: 'Step-by-step support in every class',
        },
        {
          icon: 'activity',
          text: 'Fun activities and quizzes to build confidence',
        },
        {
          icon: 'infinity',
          text: 'A playful journey from first code blocks to bigger creations',
        },
        {
          icon: 'certificate',
          text: 'STEM.org accredited certificates on completion',
        },
        {
          icon: 'file',
          text: 'Monthly progress updates for parents',
        },
        {
          icon: 'lifetime-access',
          text: 'Access to class resources and practice material',
        },
      ],
    },
    CODING_GRADE_6_8: {
      path: `${IMAGE_PATH}coding-for-kids/grade-6-8/card-image`,
      altText: 'Coding course for Grade 6-8 kids',
      includes: [
        { icon: 'session', text: 'Live coding classes in 1:1 or small groups' },
        {
          icon: 'learn',
          text: 'Experienced teachers who challenge and inspire',
        },
        {
          icon: 'modules',
          text: 'Hands-on projects in Python, web development, and AI',
        },
        {
          icon: 'personalized',
          text: 'Step-by-step support and real-time feedback in every class',
        },
        {
          icon: 'activity',
          text: 'Coding challenges, quizzes, and problem-solving activities',
        },
        {
          icon: 'infinity',
          text:
            'A structured journey from beginner coding to real-world projects',
        },
        {
          icon: 'certificate',
          text: 'STEM.org accredited certificates on completion',
        },
        { icon: 'file', text: 'Monthly progress updates for parents' },
        {
          icon: 'lifetime-access',
          text: 'Access to class resources and practice material',
        },
      ],
    },
    CODING_GRADE_9_10: {
      path: `${IMAGE_PATH}coding-for-kids/grade-9-10/card-image`,
      altText: 'Coding course for Grade 9-10 teens',
      includes: [
        { icon: 'session', text: 'Live coding classes in 1:1 or small groups' },
        {
          icon: 'learn',
          text: 'Friendly teachers who make coding fun and easy',
        },
        {
          icon: 'modules',
          text: 'Hands-on games, stories, animations, and creative projects',
        },
        { icon: 'personalized', text: 'Step-by-step support in every class' },
        {
          icon: 'activity',
          text: 'Fun activities and quizzes to build confidence',
        },
        {
          icon: 'infinity',
          text: 'A playful journey from first code blocks to bigger creations',
        },
        {
          icon: 'certificate',
          text: 'STEM.org accredited certificates on completion',
        },
        { icon: 'file', text: 'Monthly progress updates for parents' },
        {
          icon: 'lifetime-access',
          text: 'Access to class resources and practice material',
        },
      ],
    },
    CODING_GRADE_11_12: {
      path: `${IMAGE_PATH}coding-for-kids/grade-11-12/card-image`,
      altText: 'Coding course for Grade 11-12 teens',
      includes: [
        { icon: 'session', text: 'Live coding classes in 1:1 or small groups' },
        {
          icon: 'learn',
          text: 'Friendly teachers who make coding fun and easy',
        },
        {
          icon: 'modules',
          text: 'Hands-on games, stories, animations, and creative projects',
        },
        { icon: 'personalized', text: 'Step-by-step support in every class' },
        {
          icon: 'activity',
          text: 'Fun activities and quizzes to build confidence',
        },
        {
          icon: 'infinity',
          text: 'A playful journey from first code blocks to bigger creations',
        },
        {
          icon: 'certificate',
          text: 'STEM.org accredited certificates on completion',
        },
        { icon: 'file', text: 'Monthly progress updates for parents' },
        {
          icon: 'lifetime-access',
          text: 'Access to class resources and practice material',
        },
      ],
    },
    CODING_GRADE_4_5: {
      path: `${IMAGE_PATH}coding-for-kids/grade-4-5/card-image`,
      altText: 'Coding course for Grade 4-5 kids',
      includes: [
        {
          icon: 'session',
          text: 'Live coding classes in 1:1 or small groups',
        },
        {
          icon: 'learn',
          text: 'Expert teachers who make coding clear and engaging',
        },
        {
          icon: 'modules',
          text: 'Games, apps, websites, AI, and Python projects',
        },
        {
          icon: 'personalized',
          text: 'Guided support in every class',
        },
        {
          icon: 'activity',
          text: 'Quizzes and activities to strengthen concepts',
        },
        {
          icon: 'infinity',
          text: 'A structured path from visual coding to real-world creation',
        },
        {
          icon: 'certificate',
          text: 'STEM.org accredited certificates on completion',
        },
        {
          icon: 'file',
          text: 'Monthly progress updates for parents',
        },
        {
          icon: 'lifetime-access',
          text: 'Access to class resources and practice material',
        },
      ],
    },
  }

  return (
    <>
      <Modal
        isOpen={ismodalOpen}
        setIsOpen={setModalOpen}
        isCrossIconShown={true}
        shouldCloseOnEsc
        width="max-w-2xl"
      >
        <div>
          <iframe
            src={videoURL}
            scrolling="no"
            width="601"
            height="334"
            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
            className="border border-grey-600 rounded-md max-w-full"
            allowFullScreen
          />
        </div>
      </Modal>
      <div className="flex bg-white flex-col md:shadow-lg relative md:w-80 rounded-t-md">
        {!shouldHideCardImage && (
          <>
            {coursesData?.videoURL && (
              <div
                onClick={() => {
                  setModalOpen(true)
                  setVideoURL(coursesData.videoURL)
                }}
                className="p-4 md:p-3 cursor-pointer hover:shadow-lg transition-shadow duration-300"
              >
                <FastlyImage
                  src={`${imageProps[courseName].path}.png`}
                  alt={imageProps[courseName].altText}
                  width={450}
                  height={252}
                  loading="eager"
                  fetchPriority="high"
                  priority
                  className="rounded-t-md mx-auto"
                />
              </div>
            )}

            {!coursesData?.videoURL && (
              <div className="p-4 md:p-3 mx-auto">
                <FastlyImage
                  src={`${imageProps[courseName].path}.png`}
                  alt={imageProps[courseName].altText}
                  width={450}
                  height={252}
                  loading="eager"
                  fetchPriority="high"
                  priority
                />
              </div>
            )}
          </>
        )}

        <div className="flex flex-col m-5 text-grey text-sm">
          <div className="flex md:hidden">
            <div className="md:w-4/5 md:pr-4 max-w-3xl">
              <h2 className="font-700 text-2xl text-left leading-tight">
                {coursesData.name}
              </h2>
              <p className="font-600 text-left mb-0 mt-4 max-w-2xl text-base">
                {coursesData.description}
              </p>
              <p className="font-600 text-left my-5 max-w-4xl text-base">
                {coursesData.modules} Modules, {coursesData.lessons} Lessons,{' '}
                {coursesData.activities} Activities & Projects,{' '}
                {coursesData.quizzes} Quizzes
              </p>
              {!ishideStarRating && (
                <div className="flex flex-col md:flex-row md:space-x-8 space-y-5 md:space-y-0 justify-center items-center lg:justify-start">
                  <StudentCountriesOverview isTextWhite={false} />
                  <Ratings isDarkText />
                </div>
              )}
            </div>
          </div>

          <BookFreeClassBtn
            className="text-base lg:block hidden mt-8 lg:mt-0"
            query={urlQuery}
          />
          {coursesData?.curriculumPDFLink && !ishideDownloadCurriculum && (
            <>
              <a
                href={coursesData.curriculumPDFLink}
                target="_blank"
                rel="noreferrer"
                className="hidden lg:flex text-orange mt-3 items-center justify-center"
              >
                Download Curriculum <Download size={14} className="ml-2" />
              </a>
              <a
                href={coursesData.curriculumPDFLink}
                target="_blank"
                rel="noreferrer"
                className="lg:hidden"
              >
                <Button
                  className="flex items-center justify-center w-full"
                  variant="outline"
                  size="lg"
                >
                  Download Curriculum <Download size={14} className="ml-2" />
                </Button>
              </a>
            </>
          )}

          <p className="text-lg font-700 mt-5">This course includes:</p>
          <div className="space-y-2 mt-3">
            {imageProps[courseName].includes?.map(({ icon, text }) => (
              <div key={text} className="flex items-start space-x-2">
                <div className="mt-0 flex-shrink-0">
                  <FastlyImage
                    src={`${IMAGE_PATH}icons/${icon}.png`}
                    width={20}
                    height={20}
                    alt={icon}
                  />
                </div>
                <p>{text}</p>
              </div>
            ))}
            {!imageProps[courseName].includes && (
              <>
                <div className="flex items-start space-x-2">
                  <div className="mt-0 flex-shrink-0">
                    <FastlyImage
                      src={`${IMAGE_PATH}icons/session.png`}
                      width={20}
                      height={20}
                      alt="session"
                    />
                  </div>

                  <p>
                    Choose from Group or 1-on-1 Personalized AI & Coding lessons
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="mt-1">
                    <FastlyImage
                      src={`${IMAGE_PATH}icons/learn.png`}
                      width={20}
                      height={20}
                      alt="learn"
                    />
                  </div>
                  <p>Learn live with expert Computer Science teacher</p>
                </div>
                <div className="flex items-center space-x-2">
                  <FastlyImage
                    src={`${IMAGE_PATH}icons/modules.png`}
                    width={20}
                    height={20}
                    alt="modules"
                  />
                  <p>
                    {coursesData.displayName} is seamlessly integrated into the
                    curriculum, providing hands-on coding experience
                  </p>
                </div>

                {coursesData.activities && (
                  <div className="flex items-center space-x-2">
                    <FastlyImage
                      src={`${IMAGE_PATH}icons/activity.png`}
                      width={20}
                      height={20}
                      alt="activity"
                    />
                    <p>
                      Engage in {coursesData.activities} activities & projects
                      designed to enhance practical skills
                    </p>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <FastlyImage
                    src={`${IMAGE_PATH}icons/certificate.png`}
                    width={20}
                    height={20}
                    alt="certificate"
                  />
                  <p>
                    Students earn STEM.org accredited certificates, recognizing
                    their achievements
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <FastlyImage
                    src={`${IMAGE_PATH}icons/infinity.png`}
                    width={20}
                    height={20}
                    alt="infinity"
                  />
                  <p>
                    Enjoy lifetime access to course content for continuous
                    learning
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="mt-1 flex-shrink-0">
                    <FastlyImage
                      src={`${IMAGE_PATH}icons/personalized.png`}
                      width={20}
                      height={20}
                      alt="personalized"
                    />
                  </div>
                  <p>Get doubt sessions with your teacher</p>
                </div>
                <div className="flex items-center space-x-2">
                  <FastlyImage
                    src={`${IMAGE_PATH}icons/file.png`}
                    width={20}
                    height={20}
                    alt="cup"
                  />
                  <p>
                    Receive monthly progress reports to track your child&#39;s
                    growth and performance
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default CourseCard
